import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Brain, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DomainRow({ site, onForget }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const slug = site.domain.replace(/[^a-z0-9]/gi, '-');

  const toggle = async () => {
    setOpen(!open);
    if (!entries) {
      const { data } = await axios.get(`${API}/form-memory`, { params: { domain: site.domain } });
      setEntries(data.entries);
    }
  };

  const forgetEntry = async (id) => {
    await axios.delete(`${API}/form-memory/entry/${id}`);
    setEntries(entries.filter((e) => e.entry_id !== id));
    toast.success('Field forgotten');
  };

  return (
    <div className="border-2 border-border" data-testid={`memory-site-${slug}`}>
      <div className="flex items-center justify-between p-4">
        <button onClick={toggle} className="flex items-center gap-3 text-left flex-1" data-testid={`memory-site-toggle-${slug}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-mono text-sm">{site.domain}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{site.fields} fields · {site.hits} learns</span>
        </button>
        <Button variant="outline" size="sm" onClick={() => onForget(site.domain)} className="uppercase tracking-widest text-xs" data-testid={`memory-forget-site-${slug}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Forget site
        </Button>
      </div>
      {open && entries && (
        <ul className="border-t-2 border-border divide-y divide-border">
          {entries.map((e) => (
            <li key={e.entry_id} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`memory-entry-${e.entry_id}`}>
              <span className="truncate mr-3">
                <span className="text-muted-foreground">{e.label || e.signature}</span>
                <span className="mx-2 text-primary">→</span>
                <span className="font-mono">{e.key ? `profile.${e.key}` : `"${e.value}"`}</span>
              </span>
              <button onClick={() => forgetEntry(e.entry_id)} className="text-muted-foreground hover:text-destructive" aria-label="Forget field" data-testid={`memory-forget-entry-${e.entry_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const FormMemoryPanel = () => {
  const [state, setState] = useState(null);

  const load = () => axios.get(`${API}/form-memory`).then(({ data }) => setState(data)).catch(() => setState({ domains: [] }));
  useEffect(() => { load(); }, []);

  const forgetSite = async (domain) => {
    await axios.delete(`${API}/form-memory/${encodeURIComponent(domain)}`);
    toast.success(`Forgot ${domain}`);
    load();
  };

  if (!state) return null;
  const isFree = state.subscription_tier !== 'pro';

  return (
    <div className="mb-12" data-testid="form-memory-panel">
      <h2 className="font-heading text-2xl uppercase tracking-wider mb-2 flex items-center gap-2">
        <Brain className="w-6 h-6 text-primary" /> FORM MEMORY
      </h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Fix a field by hand after autofill and the extension remembers it for that site. Next time, it fills automatically.
        {isFree && ` Free plan: ${state.free_domain_limit} sites. Pro: unlimited.`}
      </p>
      {state.domains.length === 0 ? (
        <div className="border-2 border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="form-memory-empty">
          Nothing learned yet. Autofill a form, correct anything it missed, and it will show up here.
        </div>
      ) : (
        <div className="space-y-3" data-testid="form-memory-list">
          {state.domains.map((s) => <DomainRow key={s.domain} site={s} onForget={forgetSite} />)}
        </div>
      )}
    </div>
  );
};
